import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Button,
  Paper,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { collection, query, orderBy, onSnapshot, doc, getDoc, updateDoc, getDocs, writeBatch } from "firebase/firestore";
import { db, auth } from "../firebase";

export default function EmployeeAssignmentsSection({ employeeId }) {
  // Firestore collections
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  // Local assignment state
  const [assignments, setAssignments] = useState({});
  const [employeeRates, setEmployeeRates] = useState({});
  const [edit, setEdit] = useState(false);
  // Track last-saved snapshot so we can detect unsaved changes and support discard.
  const [savedPending, setSavedPending] = useState({});

  // Load all collections
  useEffect(() => {
    // Resolve tenant first, then attach listeners to tenant-scoped collections.
    let unsubLoc = null;
    let unsubDept = null;
    let unsubRoles = null;
    let mounted = true;

    (async () => {
      // Resolve tenantId from localStorage or userTenants fallback
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          // If we can't read userTenants (not authenticated or permission denied)
          // don't attach any listeners.
          console.warn('Could not resolve tenant for assignments listeners', e);
          return;
        }
      }
      if (!tenantId) return;

      try {
        const locQuery = query(collection(db, `tenants/${tenantId}/locations`), orderBy('name'));
        const deptQuery = query(collection(db, `tenants/${tenantId}/departments`), orderBy('name'));
        const roleQuery = query(collection(db, `tenants/${tenantId}/roles`), orderBy('name'));

        unsubLoc = onSnapshot(
          locQuery,
          snap => { if (!mounted) return; setLocations(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
          err => { console.warn('locations listener error', err); setLocations([]); }
        );

        unsubDept = onSnapshot(
          deptQuery,
          snap => { if (!mounted) return; setDepartments(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(dd => !dd.archived)); },
          err => { console.warn('departments listener error', err); setDepartments([]); }
        );

        unsubRoles = onSnapshot(
          roleQuery,
          snap => { if (!mounted) return; setRoles(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => !r.archived)); },
          err => { console.warn('roles listener error', err); setRoles([]); }
        );
      } catch (e) {
        console.warn('Failed to attach assignments listeners', e);
      }
    })();

    return () => {
      mounted = false;
      try { if (typeof unsubLoc === 'function') unsubLoc(); } catch (e) {}
      try { if (typeof unsubDept === 'function') unsubDept(); } catch (e) {}
      try { if (typeof unsubRoles === 'function') unsubRoles(); } catch (e) {}
    };
  }, []);

  // Load assignments from employee doc
  useEffect(() => {
    if (!employeeId) return;
    let mounted = true;
    (async () => {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          // can't resolve tenant
          return;
        }
      }
      if (!tenantId) return;

      // Listen to employee doc in realtime so rates/assignments changes propagate immediately
      const empRef = doc(db, `tenants/${tenantId}/employees`, employeeId);
      const unsub = onSnapshot(empRef, snap => {
        if (!mounted) return;
        if (snap.exists()) {
          const data = snap.data();
          // keep rates in sync from employee doc; assignments will be driven from flattened subcollection
          setEmployeeRates(data.rates || {});
        }
      }, err => {
        console.warn('employee doc listener error', err);
      });

      // cleanup subscriber when component unmounts or employeeId changes
      return () => { mounted = false; try { if (typeof unsub === 'function') unsub(); } catch (e) {} };
    })();
  }, [employeeId]);

  // Listen to flattened assignments subcollection and build nested assignments map for the UI
  useEffect(() => {
    if (!employeeId) return;
    let mounted = true;

    (async () => {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          // can't resolve tenant
          return;
        }
      }
      if (!tenantId) return;

      try {
        const colRef = collection(db, `tenants/${tenantId}/employees`, employeeId, 'assignments');
        const unsubAssign = onSnapshot(colRef, snap => {
          if (!mounted) return;
          const nested = {};
          snap.docs.forEach(d => {
            const data = d.data();
            const locId = data.locationId;
            const roleId = data.roleId;
            if (!locId || !roleId) return;

            // prefer departmentId from the flattened doc, otherwise fall back to role's department
            const flatDeptId = data.departmentId;
            const roleDoc = roles.find(r => r.id === roleId) || {};
            const resolvedDeptId = flatDeptId || roleDoc.departmentId || '';

            if (!nested[locId]) nested[locId] = { departments: {} };
            if (data.locationName) nested[locId].locationName = data.locationName;

            if (!nested[locId].departments[resolvedDeptId]) nested[locId].departments[resolvedDeptId] = { appearOnSchedule: data.appearsOnSchedule !== undefined ? data.appearsOnSchedule : true, roles: {} };

            nested[locId].departments[resolvedDeptId].roles[roleId] = {
              assigned: data.assigned === undefined ? true : !!data.assigned,
              isPrimary: !!data.isPrimary,
              roleName: data.roleName || roleDoc.name || ''
            };
          });

          setAssignments(nested);
        }, err => {
          console.warn('assignments subcollection listener error', err);
        });

        // cleanup
        return () => { try { unsubAssign(); } catch (e) {} };
      } catch (e) {
        console.warn('Failed to attach assignments subcollection listener', e);
      }
    })();

    return () => { mounted = false; };
  }, [employeeId, departments, roles]);

  // Local UI state for checkboxes
  const [pending, setPending] = useState({});
  // Sanitize assignments whenever assignments, departments, or roles change
  const sanitizeAssignments = (src = {}) => {
    const out = {};
    Object.entries(src || {}).forEach(([locId, locObj]) => {
      if (!locObj || !locObj.departments) return;

      // Find location doc to capture name
      const locDoc = locations.find(l => l.id === locId);

      const deptOut = {};
      Object.entries(locObj.departments).forEach(([deptId, deptObj]) => {
        // Dept must exist and not be archived
        const deptDoc = departments.find(d => d.id === deptId);
        if (!deptDoc || deptDoc.archived) return;
        const rolesField = deptObj.roles || {};
        if (Array.isArray(rolesField)) {
          const kept = rolesField.filter(roleId => {
            const r = roles.find(rr => rr.id === roleId);
            return r && !r.archived;
          });
          if (kept.length > 0) {
            // convert to object form and include roleName
            const roleObjOut = {};
            kept.forEach(roleId => {
              const r = roles.find(rr => rr.id === roleId);
              roleObjOut[roleId] = { assigned: true, isPrimary: false, roleName: r ? r.name : '' };
            });
            deptOut[deptId] = { ...deptObj, roles: roleObjOut };
          }
        } else {
          const roleObjOut = {};
          Object.entries(rolesField).forEach(([roleId, meta]) => {
            const r = roles.find(rr => rr.id === roleId);
            if (r && !r.archived && meta && meta.assigned) roleObjOut[roleId] = { ...meta, roleName: r.name };
          });
          if (Object.keys(roleObjOut).length > 0) deptOut[deptId] = { ...deptObj, roles: roleObjOut };
        }
      });
      if (Object.keys(deptOut).length > 0) {
        out[locId] = { departments: deptOut };
        if (locDoc && locDoc.name) out[locId].locationName = locDoc.name;
      }
    });
    return out;
  };

  useEffect(() => {
    const next = sanitizeAssignments(assignments);
    setPending(next);
    setSavedPending(next);
    setEdit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, departments, roles]);

  const normalizeForCompare = useCallback(obj => {
    // Stable stringify by sorting keys at each level.
    const sortObject = value => {
      if (Array.isArray(value)) return value.map(sortObject);
      if (value && typeof value === 'object') {
        return Object.keys(value)
          .sort()
          .reduce((acc, k) => {
            acc[k] = sortObject(value[k]);
            return acc;
          }, {});
      }
      return value;
    };
    try {
      return JSON.stringify(sortObject(obj || {}));
    } catch (e) {
      return JSON.stringify(obj || {});
    }
  }, []);

  const isDirty = normalizeForCompare(pending) !== normalizeForCompare(savedPending);

  const getPillTextColor = useCallback((bg = "#888") => {
    // Determine readable text color for a hex background.
    // Supports #RGB and #RRGGBB.
    const hex = (bg || "").toString().trim();
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
    if (!m) return '#fff';
    let h = m[1];
    if (h.length === 3) h = h.split('').map(ch => ch + ch).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    // Relative luminance
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return luminance > 0.6 ? '#111' : '#fff';
  }, []);

  // Keep flattened assignment docs' wage field in sync when employeeRates or roles change.
  useEffect(() => {
    if (!employeeId) return;

    let mounted = true;
    (async () => {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          return;
        }
      }
      if (!tenantId) return;

      try {
  // debug logging removed
        const assignmentsCol = collection(db, `tenants/${tenantId}/employees`, employeeId, 'assignments');
        const snap = await getDocs(assignmentsCol);
        const updates = [];

        snap.forEach(d => {
          if (!mounted) return;
          const data = d.data();
          const roleId = data.roleId;

          // Compute wage using same logic as handleSave
          let newWage = null;
          if (employeeRates) {
            if (Array.isArray(employeeRates)) {
              const found = employeeRates.find(r => (r && (r[roleId] !== undefined || r.roleId === roleId || r.id === roleId)));
              if (found) {
                if (found[roleId] !== undefined) newWage = found[roleId];
                else if (found.wage !== undefined) newWage = found.wage;
                else if (found.rate !== undefined) newWage = found.rate;
              }
            } else if (Object.prototype.hasOwnProperty.call(employeeRates, roleId)) {
              newWage = employeeRates[roleId];
            }
          }
          if (newWage === null || newWage === undefined) {
            const roleDoc = roles.find(rr => rr.id === roleId);
            if (roleDoc) {
              if (roleDoc.defaultWage !== undefined) newWage = roleDoc.defaultWage;
              else if (roleDoc.wage !== undefined) newWage = roleDoc.wage;
              else if (roleDoc.rate !== undefined) newWage = roleDoc.rate;
            }
          }

          // If wage differs (including null vs number), update the doc
          const existingWage = data.wage === undefined ? null : data.wage;
          const differs = (existingWage !== newWage) && !(Number.isNaN(existingWage) && Number.isNaN(newWage));
          if (differs) {
            const dref = doc(db, `tenants/${tenantId}/employees`, employeeId, 'assignments', d.id);
            updates.push(updateDoc(dref, { wage: newWage }));
          }
        });

        if (updates.length > 0) await Promise.all(updates);
      } catch (e) {
        // ignore sync errors but log
        console.warn('Failed to sync assignment wages', e);
      }
    })();

    return () => { mounted = false; };
  }, [employeeId, employeeRates, roles]);

  // Helpers for building nested structure
  const isLocAssigned = locId => !!pending?.[locId];
  const isDeptAssigned = (locId, deptId) => !!pending?.[locId]?.departments?.[deptId];
  const isRoleAssigned = (locId, deptId, roleId) => {
    const roles = pending?.[locId]?.departments?.[deptId]?.roles;
    if (Array.isArray(roles)) {
      // Legacy format - convert to new format
      return roles.includes(roleId);
    }
    return !!roles?.[roleId]?.assigned;
  };
  const isRolePrimary = (locId, deptId, roleId) => {
    const roles = pending?.[locId]?.departments?.[deptId]?.roles;
    if (Array.isArray(roles)) return false; // Legacy format has no primary concept
    return !!roles?.[roleId]?.isPrimary;
  };

  // UI change handlers
  const toggleLoc = locId => {
  if (!edit) setEdit(true);
    setPending(prev => {
      const newObj = { ...(prev || {}) };
      if (newObj[locId]) {
        delete newObj[locId];
      } else {
        newObj[locId] = { departments: {} };
      }
      return newObj;
    });
  };

  const toggleDept = (locId, deptId) => {
  if (!edit) setEdit(true);
    setPending(prev => {
      // Deep clone location and departments
      const newObj = { ...(prev || {}) };
      newObj[locId] = {
        departments: { ...(prev?.[locId]?.departments || {}) }
      };
      if (newObj[locId].departments[deptId]) {
        delete newObj[locId].departments[deptId];
      } else {
        newObj[locId].departments[deptId] = { appearOnSchedule: true, roles: {} };
      }
      return newObj;
    });
  };

  const toggleRole = (locId, deptId, roleId) => {
  if (!edit) setEdit(true);
    setPending(prev => {
      // Deep clone location and departments
      const newObj = { ...(prev || {}) };
      // Ensure location container exists (auto-assign location when interacting with roles)
      const prevLoc = prev?.[locId];
      const prevDepartments = prevLoc?.departments || {};
      newObj[locId] = {
        ...(prevLoc || {}),
        departments: { ...prevDepartments }
      };
      
      // Ensure department container exists (auto-assign department when interacting with roles)
      const prevDeptObj = prevDepartments[deptId] || { appearOnSchedule: true, roles: {} };
      const deptObj = { ...prevDeptObj };
      
      // Handle legacy array format
      if (Array.isArray(prevDeptObj.roles)) {
        // Convert legacy array to new object format
        const newRoles = {};
        prevDeptObj.roles.forEach(id => {
          newRoles[id] = { assigned: true, isPrimary: false };
        });
        deptObj.roles = newRoles;
      } else {
        deptObj.roles = { ...prevDeptObj.roles };
      }
      
      // Toggle role assignment
      if (deptObj.roles[roleId]?.assigned) {
        delete deptObj.roles[roleId];
      } else {
        deptObj.roles[roleId] = { assigned: true, isPrimary: false };
      }
      
      newObj[locId].departments[deptId] = deptObj;
      return newObj;
    });
  };

  const togglePrimaryRole = (locId, deptId, roleId) => {
  if (!edit) setEdit(true);
    setPending(prev => {
      // Deep clone location and departments
      const newObj = { ...(prev || {}) };
      // Ensure location container exists
      const prevLoc = prev?.[locId];
      const prevDepartments = prevLoc?.departments || {};
      newObj[locId] = {
        ...(prevLoc || {}),
        departments: { ...prevDepartments }
      };
      
      // Deep clone department object
      const prevDeptObj = prevDepartments[deptId] || { appearOnSchedule: true, roles: {} };
      const deptObj = { ...prevDeptObj };
      
      // Handle legacy array format
      if (Array.isArray(prevDeptObj.roles)) {
        const newRoles = {};
        prevDeptObj.roles.forEach(id => {
          newRoles[id] = { assigned: true, isPrimary: false };
        });
        deptObj.roles = newRoles;
      } else {
        deptObj.roles = { ...prevDeptObj.roles };
      }
      
      // If role is not assigned, assign it first
      if (!deptObj.roles[roleId]?.assigned) {
        deptObj.roles[roleId] = { assigned: true, isPrimary: true };
      } else {
        // Clear any existing primary role in this department
        Object.keys(deptObj.roles).forEach(id => {
          if (deptObj.roles[id] && deptObj.roles[id].isPrimary) {
            deptObj.roles[id] = { ...deptObj.roles[id], isPrimary: false };
          }
        });
        
        // Set this role as primary
        deptObj.roles[roleId] = { 
          ...deptObj.roles[roleId], 
          isPrimary: !isRolePrimary(locId, deptId, roleId) 
        };
      }
      
      newObj[locId].departments[deptId] = deptObj;
      return newObj;
    });
  };

  // Group departments/roles by location for UI
  const deptsByLoc = useCallback(locId => {
    const filtered = departments.filter(d => d.locationId === locId);
    return filtered;
  }, [departments]);
  const rolesByDept = useCallback(
    deptId => roles.filter(r => r.departmentId === deptId),
    [roles]
  );

  // Save logic: writes to Firestore
  const handleSave = async () => {
    if (!employeeId) return;
    // Resolve tenant
    let tenantId = null;
    const adminRaw = localStorage.getItem('adminUser');
    if (adminRaw) {
      try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
    }
    if (!tenantId && auth && auth.currentUser) {
      const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
      const utSnap = await getDoc(utRef);
      if (utSnap.exists()) {
        const ut = utSnap.data();
        tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
      }
    }
    if (!tenantId) return;
    // Sanitize pending assignments to remove any archived or missing depts/roles
    const sanitizeAssignments = (src = {}) => {
      const out = {};
      Object.entries(src).forEach(([locId, locObj]) => {
        if (!locObj || !locObj.departments) return;

        // Find location doc to capture name
        const locDoc = locations.find(l => l.id === locId);

        const deptOut = {};
        Object.entries(locObj.departments).forEach(([deptId, deptObj]) => {
          // Dept must exist and not be archived
          const deptDoc = departments.find(d => d.id === deptId);
          if (!deptDoc || deptDoc.archived) return;
          // Roles can be legacy array or object
          const rolesField = deptObj.roles || {};
          if (Array.isArray(rolesField)) {
            const kept = rolesField.filter(roleId => {
              const r = roles.find(rr => rr.id === roleId);
              return r && !r.archived;
            });
            if (kept.length > 0) {
              // Map legacy array into object with role metadata including roleName
              const roleObjOut = {};
              kept.forEach(roleId => {
                const r = roles.find(rr => rr.id === roleId);
                roleObjOut[roleId] = { assigned: true, isPrimary: false, roleName: r ? r.name : '' };
              });
              deptOut[deptId] = { ...deptObj, roles: roleObjOut };
            }
          } else {
            // object format
            const roleObjOut = {};
            Object.entries(rolesField).forEach(([roleId, meta]) => {
              const r = roles.find(rr => rr.id === roleId);
              if (r && !r.archived && meta && meta.assigned) {
                roleObjOut[roleId] = { ...meta, roleName: r.name };
              }
            });
            if (Object.keys(roleObjOut).length > 0) {
              deptOut[deptId] = { ...deptObj, roles: roleObjOut };
            }
          }
        });
        if (Object.keys(deptOut).length > 0) {
          out[locId] = { departments: deptOut };
          // Attach the locationName at the top level for easier flattened consumption
          if (locDoc && locDoc.name) out[locId].locationName = locDoc.name;
        }
      });
      return out;
    };

    const sanitized = sanitizeAssignments(pending);

    // Sync flattened assignments subcollection for this employee
    const assignmentsCol = collection(db, `tenants/${tenantId}/employees`, employeeId, 'assignments');

    // Read existing assignment docs so we can delete ones no longer present
    const existingSnap = await getDocs(assignmentsCol);
    const existingDocs = {};
    existingSnap.forEach(d => existingDocs[d.id] = d.data());

    // Build target map from sanitized pending. We'll use a deterministic doc id: `${locId}_${roleId}`
    const targetMap = {};
    Object.entries(sanitized).forEach(([locId, locObj]) => {
      const locName = locObj.locationName || '';
  const deptMap = locObj.departments || {};
  Object.entries(deptMap).forEach(([deptId, deptObj]) => {
        const rolesField = deptObj.roles || {};
        // rolesField should be object-format now
        Object.entries(rolesField).forEach(([roleId, meta]) => {
          const docId = `${locId}_${roleId}`;
          // Resolve wage from employeeRates. Support rates stored as an object mapping roleId->wage
          // and treat 0 as a valid wage. Fall back to null when not present.
          let wage = null;
          if (employeeRates) {
            if (Array.isArray(employeeRates)) {
              // If rates is an array of objects, try to find an entry with roleId key or { roleId, wage }
              const found = employeeRates.find(r => (r && (r[roleId] !== undefined || r.roleId === roleId || r.id === roleId)));
              if (found) {
                if (found[roleId] !== undefined) wage = found[roleId];
                else if (found.wage !== undefined) wage = found.wage;
                else if (found.rate !== undefined) wage = found.rate;
              }
            } else if (Object.prototype.hasOwnProperty.call(employeeRates, roleId)) {
              wage = employeeRates[roleId];
            }
          }
          // If wage still null/undefined, try to fallback to the role document's default wage
          if (wage === null || wage === undefined) {
            const roleDoc = roles.find(rr => rr.id === roleId);
            if (roleDoc) {
              if (roleDoc.defaultWage !== undefined) wage = roleDoc.defaultWage;
              else if (roleDoc.wage !== undefined) wage = roleDoc.wage;
              else if (roleDoc.rate !== undefined) wage = roleDoc.rate;
            }
          }
          targetMap[docId] = {
            locationId: locId,
            locationName: locName,
            departmentId: deptId || null,
            departmentName: (departments.find(d => d.id === deptId) || {}).name || '',
            roleId,
            roleName: meta.roleName || '',
            wage,
            appearsOnSchedule: !!deptObj.appearOnSchedule,
            isPrimary: !!meta.isPrimary,
            assigned: true
          };
        });
      });
    });

  // targetMap built (debug logging removed)

    // Use a batched write to atomically upsert/delete assignment docs and update employee doc
    const batch = writeBatch(db);

    // Upsert targetMap entries
    Object.entries(targetMap).forEach(([docId, payload]) => {
      const docRef = doc(db, `tenants/${tenantId}/employees`, employeeId, 'assignments', docId);
      const existing = existingDocs[docId];
      // If exists and matches, skip; otherwise set in batch
      if (!existing || JSON.stringify(existing) !== JSON.stringify(payload)) {
        batch.set(docRef, payload, { merge: true });
      }
      delete existingDocs[docId]; // mark as processed
    });

    // Delete any remaining existing assignment docs that are no longer present
    Object.keys(existingDocs).forEach(id => {
      const dref = doc(db, `tenants/${tenantId}/employees`, employeeId, 'assignments', id);
      batch.delete(dref);
    });

  // NOTE: we intentionally do NOT update the nested `assignments` field on the employee doc here.
  // The flattened `assignments` subcollection is the canonical source. Keep local state in sync only.

    // Commit the batch
    await batch.commit();

    // Update local state
    setAssignments(sanitized);
    setSavedPending(sanitized);
    setEdit(false);
  };

  const handleDiscard = () => {
    setPending(savedPending);
    setEdit(false);
  };

  // --- Three-column interactive layout ---
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  useEffect(() => {
    // Auto-select first location if none selected
    if (!selectedLocationId && locations.length > 0) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  // If assignments exist but no location selected, prefer first location that has assignments
  useEffect(() => {
    if (!selectedLocationId && assignments) {
      const locIds = Object.keys(assignments || {});
      if (locIds.length > 0) {
        // prefer a location that exists in locations list
        const found = locIds.find(id => locations.some(l => l.id === id));
        if (found) setSelectedLocationId(found);
        else setSelectedLocationId(locations[0]?.id || null);
      }
    }
  }, [assignments, locations, selectedLocationId]);

  useEffect(() => {
    // Keep selectedDepartmentId in sync with location changes for backward compatibility,
    // but the UI no longer drills into a single department.
    if (!selectedLocationId) return;
    const depts = deptsByLoc(selectedLocationId);
    if (depts.length === 0) {
      if (selectedDepartmentId) setSelectedDepartmentId(null);
      return;
    }
    if (!selectedDepartmentId || !depts.some(d => d.id === selectedDepartmentId)) {
      setSelectedDepartmentId(depts[0].id);
    }
  }, [selectedLocationId, departments, selectedDepartmentId, deptsByLoc]);

  const assignedLocationCount = Object.keys(pending || {}).length;
  const assignedDepartmentCount = Object.values(pending || {}).reduce((total, location) => {
    return total + Object.keys(location?.departments || {}).length;
  }, 0);
  const assignedRoleCount = Object.values(pending || {}).reduce((total, location) => {
    return total + Object.values(location?.departments || {}).reduce((deptTotal, dept) => {
      return deptTotal + Object.values(dept?.roles || {}).filter((meta) => meta?.assigned).length;
    }, 0);
  }, 0);

  return (
    <Paper sx={{ 
      p: 0, 
      minHeight: 420, 
      borderRadius: 4,
      boxShadow: '0 20px 48px rgba(15, 23, 42, 0.08)',
      overflow: 'hidden',
      border: '1px solid rgba(148, 163, 184, 0.18)',
      bgcolor: 'rgba(248,250,252,0.82)',
      backdropFilter: 'blur(10px)',
    }}>
      <Box sx={{
        p: { xs: 2.2, md: 3 },
        background: 'linear-gradient(135deg, rgba(37,99,235,0.96) 0%, rgba(59,130,246,0.9) 50%, rgba(13,148,136,0.82) 100%)',
        borderBottom: '1px solid',
        borderColor: 'rgba(255,255,255,0.16)',
        color: 'common.white',
      }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ lg: 'flex-start' }} spacing={2}>
          <Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: 1.5, opacity: 0.82 }}>
              STAFFING STRUCTURE
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, mt: 0.55, lineHeight: 1.04 }}>
              Employee Assignments
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 1, maxWidth: 720, lineHeight: 1.75 }}>
              Assign locations, departments, and roles to this employee
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(3, minmax(120px, 1fr))' },
              gap: 1,
              width: { xs: '100%', lg: 'auto' },
              minWidth: { lg: 380 },
            }}
          >
            {[
              { label: 'Locations', value: assignedLocationCount },
              { label: 'Departments', value: assignedDepartmentCount },
              { label: 'Roles', value: assignedRoleCount },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  p: 1.2,
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.18)',
                  bgcolor: 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: 1, opacity: 0.8 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ mt: 0.35, fontWeight: 900, fontSize: '1.5rem' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>
      </Box>
      <Box sx={{ p: { xs: 1.2, md: 1.6 } }}>
      <Box sx={{ 
        border: '1px solid rgba(148, 163, 184, 0.18)', 
        borderRadius: 4, 
        overflow: 'hidden', 
        bgcolor: 'rgba(255,255,255,0.84)',
        boxShadow: '0 14px 32px rgba(15, 23, 42, 0.05)'
      }}>
        <Box sx={{ display: 'flex', minHeight: 380 }}>
          {/* Locations column */}
          <Box sx={{ 
            minWidth: 220, 
            borderRight: '1px solid', 
            borderColor: 'rgba(148, 163, 184, 0.18)', 
            bgcolor: 'rgba(248,250,252,0.74)' 
          }}>
            <Box sx={{ p: 2.25, pb: 1.5, borderBottom: '1px solid', borderColor: 'rgba(148, 163, 184, 0.18)' }}>
              <Typography sx={{ fontWeight: 900, fontSize: '1.15rem' }}>
                Locations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.5 }}>
                Choose where this employee can work.
              </Typography>
            </Box>
            <Box sx={{ p: 1 }}>
              {locations.map(loc => (
                <Box
                  key={loc.id}
                  sx={{
                    display: 'flex', 
                    alignItems: 'center', 
                    p: 1.5, 
                    cursor: edit ? 'pointer' : 'default',
                    bgcolor: selectedLocationId === loc.id ? alpha('#2563eb', 0.08) : 'transparent',
                    borderRadius: 3,
                    mb: 0.5,
                    border: '1px solid',
                    borderColor: selectedLocationId === loc.id ? 'primary.main' : 'rgba(148, 163, 184, 0.12)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': edit ? {
                      bgcolor: selectedLocationId === loc.id ? alpha('#2563eb', 0.12) : 'rgba(248,250,252,0.92)',
                      borderColor: 'primary.light',
                      transform: 'translateY(-1px)'
                    } : {}
                  }}
                  onClick={() => setSelectedLocationId(loc.id)}
                >
                  <Checkbox
                    checked={isLocAssigned(loc.id)}
                    disabled={false}
                    onChange={e => { e.stopPropagation(); toggleLoc(loc.id); }}
                    size="small"
                    sx={{ mr: 1.5, p: 0 }}
                  />
                  <Typography 
                    variant="body2"
                    sx={{
                      color: selectedLocationId === loc.id ? 'primary.main' : 'text.primary', 
                      fontWeight: selectedLocationId === loc.id ? 600 : 500,
                      fontSize: '0.9rem'
                    }}
                  >
                    {loc.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          
          {/* Departments column */}
          <Box sx={{ 
            minWidth: 240, 
            borderRight: '1px solid', 
            borderColor: 'rgba(148, 163, 184, 0.18)', 
            bgcolor: 'rgba(255,255,255,0.74)' 
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 2.5, 
              pb: 1.5,
              borderBottom: '1px solid', 
              borderColor: 'rgba(148, 163, 184, 0.18)'
            }}>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: '1.15rem' }}>Departments</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  Enable the departments this employee belongs to.
                </Typography>
              </Box>
              {!selectedLocationId ? (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    px: 1,
                    py: 0.25,
                    bgcolor: 'rgba(15,23,42,0.06)',
                    borderRadius: 999
                  }}
                >
                  Select a location
                </Typography>
              ) : deptsByLoc(selectedLocationId).length === 0 ? (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    px: 1,
                    py: 0.25,
                    bgcolor: 'rgba(15,23,42,0.06)',
                    borderRadius: 999
                  }}
                >
                  No departments
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ p: 1 }}>
              {selectedLocationId && deptsByLoc(selectedLocationId).map(dept => (
                <Box
                  key={dept.id}
                  sx={{
                    display: 'flex', 
                    alignItems: 'center', 
                    p: 1.5, 
                    cursor: 'pointer',
                    bgcolor: isDeptAssigned(selectedLocationId, dept.id) ? alpha('#2563eb', 0.06) : 'transparent',
                    borderRadius: 3,
                    mb: 0.5,
                    border: '1px solid',
                    borderColor: isDeptAssigned(selectedLocationId, dept.id) ? 'primary.light' : 'rgba(148, 163, 184, 0.12)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(248,250,252,0.92)',
                      borderColor: 'primary.light',
                      transform: 'translateY(-1px)'
                    }
                  }}
                  onClick={() => selectedLocationId && toggleDept(selectedLocationId, dept.id)}
                >
                  <Checkbox
                    checked={isDeptAssigned(selectedLocationId, dept.id)}
                    readOnly
                    disabled={!selectedLocationId}
                    onClick={e => { e.stopPropagation(); toggleDept(selectedLocationId, dept.id); }}
                    size="small"
                    sx={{ mr: 1.5, p: 0 }}
                  />
                  <Typography 
                    variant="body2"
                    sx={{
                      color: isDeptAssigned(selectedLocationId, dept.id) ? 'text.primary' : 'text.secondary',
                      fontWeight: isDeptAssigned(selectedLocationId, dept.id) ? 600 : 500,
                      fontSize: '0.9rem'
                    }}
                  >
                    {dept.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {/* Roles column: show all departments for selected location, grouped */}
          {/* Roles column */}
          <Box sx={{ flex: 1, minWidth: 280, bgcolor: 'rgba(255,255,255,0.9)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.3, pb: 1.2, borderBottom: '1px solid', borderColor: 'rgba(148, 163, 184, 0.18)' }}>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: '1.15rem' }}>Roles</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  Assign role access inside each department and set the primary role when needed.
                </Typography>
              </Box>
              {!selectedLocationId ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    px: 1,
                    py: 0.25,
                    bgcolor: 'rgba(15,23,42,0.06)',
                    borderRadius: 999
                  }}
                >
                  Select a location
                </Typography>
              ) : null}
            </Box>
            {selectedLocationId && deptsByLoc(selectedLocationId).length === 0 ? (
              <Box sx={{ px: 2, pb: 3 }}>
                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: 'rgba(248,250,252,0.92)',
                    borderRadius: 3,
                    border: '1px dashed',
                    borderColor: 'rgba(148, 163, 184, 0.25)'
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    No departments available for this location
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {selectedLocationId && deptsByLoc(selectedLocationId).map(dept => (
              <Box
                key={dept.id}
                sx={{
                  mb: 2.5,
                  pb: 2.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 'none' }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    mb: 1
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 800,
                      fontSize: '0.78rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.8
                    }}
                  >
                    {dept.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      px: 1,
                      py: 0.25,
                      bgcolor: 'rgba(15,23,42,0.06)',
                      borderRadius: 999
                    }}
                  >
                    {rolesByDept(dept.id).length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 2 }}>
                  {rolesByDept(dept.id).length > 0 ? (
                    rolesByDept(dept.id).map(role => (
                      <Box
                        key={role.id}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          p: 1.5, 
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: isRolePrimary(selectedLocationId, dept.id, role.id) 
                            ? 'primary.main' 
                            : 'rgba(148, 163, 184, 0.18)',
                          bgcolor: isRolePrimary(selectedLocationId, dept.id, role.id) 
                            ? alpha('#2563eb', 0.07) 
                            : isRoleAssigned(selectedLocationId, dept.id, role.id)
                              ? 'rgba(248,250,252,0.92)'
                              : 'rgba(255,255,255,0.9)',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': (isDeptAssigned(selectedLocationId, dept.id)) ? {
                            borderColor: isRolePrimary(selectedLocationId, dept.id, role.id) 
                              ? 'primary.dark' 
                              : 'primary.light',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 10px 20px rgba(15, 23, 42, 0.08)'
                          } : {}
                        }}
                      >
                        <Checkbox
                          checked={isRoleAssigned(selectedLocationId, dept.id, role.id)}
                          disabled={!selectedLocationId}
                          onChange={() => toggleRole(selectedLocationId, dept.id, role.id)}
                          size="small"
                          sx={{ 
                            color: role.color || '#888', 
                            '&.Mui-checked': { color: role.color || '#888' },
                            p: 0
                          }}
                        />
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              maxWidth: '100%',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 999,
                              bgcolor: role.color || '#888',
                              color: getPillTextColor(role.color || '#888'),
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              lineHeight: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={role.name}
                          >
                            {role.name}
                          </Box>
                        </Box>

                        {isRoleAssigned(selectedLocationId, dept.id, role.id) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                            {isRolePrimary(selectedLocationId, dept.id, role.id) && (
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                px: 1,
                                py: 0.35,
                                borderRadius: 999,
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText'
                              }}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700 }}>
                                  ⭐ PRIMARY
                                </Typography>
                              </Box>
                            )}
                            <Button
                              size="small"
                              variant={isRolePrimary(selectedLocationId, dept.id, role.id) ? "outlined" : "contained"}
                              color="primary"
                              disabled={false}
                              onClick={() => togglePrimaryRole(selectedLocationId, dept.id, role.id)}
                              sx={{ 
                                minWidth: 'auto',
                                px: 1.5,
                                py: 0.5,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 999,
                                ...(isRolePrimary(selectedLocationId, dept.id, role.id) && {
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  bgcolor: 'transparent',
                                  '&:hover': {
                                    bgcolor: 'primary.50'
                                  }
                                })
                              }}
                            >
                              {isRolePrimary(selectedLocationId, dept.id, role.id) ? "Remove" : "Set Primary"}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      bgcolor: 'rgba(248,250,252,0.92)',
                      borderRadius: 3,
                      border: '1px dashed',
                      borderColor: 'rgba(148, 163, 184, 0.25)'
                    }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No roles available for this department
                      </Typography>
                    </Box>
                  )}

                  {!isDeptAssigned(selectedLocationId, dept.id) && rolesByDept(dept.id).length > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        mt: 0.5
                      }}
                    >
                      Assign this department to enable role selection
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {isDirty && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.96)',
              borderTop: '1px solid',
              borderColor: 'rgba(148, 163, 184, 0.18)',
              px: 2,
              py: 1.5
            }}
          >
            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <Button
                variant="outlined"
                  color="secondary"
                  onClick={handleDiscard}
                  sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Discard changes
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Save changes
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
      </Box>
    </Paper>
  );
}
